import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    document.addEventListener('click', this.#onDocumentClick);
    this.refs.menuDrawer.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('click', this.#onDocumentClick);
    this.refs.menuDrawer.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Close the drawer when clicking outside the component (e.g. on the header)
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.isOpen) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.close();
  };

  /**
   * Close the drawer when clicking on the backdrop (the area outside the content panel)
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.menuDrawer) this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Desktop split layout: activate L1 submenu in the right column without slide animation
    if (this.#isDesktop() && target === 'submenu') {
      event?.preventDefault(); // Prevent native <details> toggle
      this.#activateDesktopSubmenu(details);
      return;
    }

    summary.setAttribute('aria-expanded', 'true');

    // Set the drawer top offset to the actual bottom of the header, accounting for any
    // announcement bar or other content above the header that may or may not be in view.
    const header = /** @type {HTMLElement | null} */ (document.querySelector('header-component'));
    if (header) {
      const bottom = header.getBoundingClientRect().bottom;
      this.style.setProperty('--drawer-offset-top', `${Math.max(0, bottom)}px`);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Show the backdrop in the top layer so mix-blend-mode: multiply can
      // blend against the page (escapes the header's stacking context).
      if (details === this.refs.details) {
        this.#showBackdrop();
      }

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // When main drawer opens at desktop, auto-activate the first L1 submenu
      if (!target && this.#isDesktop()) {
        const firstContainer = /** @type {HTMLDetailsElement | null} */ (
          this.querySelector('.menu-drawer__menu-container')
        );
        if (firstContainer) this.#activateDesktopSubmenu(firstContainer);
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    if (details === this.refs.details) {
      this.#hideBackdrop();
    }
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach((el) => reset(/** @type {HTMLDetailsElement} */ (el)));
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      if(element instanceof HTMLElement) {
        element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Returns true when the viewport is at the desktop breakpoint (≥990px).
   * @returns {boolean}
   */
  #isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  /**
   * Promote the backdrop element to the top layer via the Popover API so
   * mix-blend-mode: multiply can composite against the page (it would
   * otherwise be isolated by header-component's stacking context). The
   * drawer panel itself is also a popover so it paints above the backdrop.
   */
  #showBackdrop() {
    this.#showPopover(this.querySelector('.menu-drawer__backdrop'));
    this.#showPopover(this.refs.menuDrawer);
  }

  /**
   * Hide the top-layer backdrop and drawer-panel popovers.
   */
  #hideBackdrop() {
    this.#hidePopover(this.refs.menuDrawer);
    this.#hidePopover(this.querySelector('.menu-drawer__backdrop'));
  }

  /**
   * @param {Element | null} el
   */
  #showPopover(el) {
    if (!(el instanceof HTMLElement)) return;
    if (typeof el.showPopover !== 'function') return;
    if (el.matches?.(':popover-open')) return;
    try {
      el.showPopover();
    } catch {
      /* already open or popover not supported */
    }
  }

  /**
   * @param {Element | null} el
   */
  #hidePopover(el) {
    if (!(el instanceof HTMLElement)) return;
    if (typeof el.hidePopover !== 'function') return;
    if (!el.matches?.(':popover-open')) return;
    try {
      el.hidePopover();
    } catch {
      /* already closed */
    }
  }

  /**
   * Desktop-only: exclusively activates a single L1 submenu in the right column.
   * Closes any previously active sibling submenu and opens the given one.
   * Clicking the already-active item keeps it open (no toggle-off).
   * @param {HTMLDetailsElement} details
   */
  #activateDesktopSubmenu(details) {
    // Close all other open L1 submenus
    this.querySelectorAll('.menu-drawer__menu-container.menu-open').forEach((el) => {
      if (el === details) return;
      el.classList.remove('menu-open');
      el.removeAttribute('open');
      el.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });

    // Already active — keep it open
    if (details.classList.contains('menu-open')) return;

    details.classList.add('menu-open');
    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * A custom element that manages the horizontal tab navigation inside 3-level drawer submenus.
 * Switching a tab hides the previous panel and shows the new one.
 */
class DrawerTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onTabClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onTabClick);
  }

  /** @param {MouseEvent} event */
  #onTabClick = (event) => {
    const btn = /** @type {HTMLElement|null} */ (/** @type {Element} */ (event.target)?.closest('.menu-drawer__tab-btn'));
    if (!btn) return;

    const tabId = btn.dataset.tab;
    if (!tabId) return;

    // Update buttons
    this.querySelectorAll('.menu-drawer__tab-btn').forEach((b) => {
      const el = /** @type {HTMLElement} */ (b);
      const active = el.dataset.tab === tabId;
      el.classList.toggle('menu-drawer__tab-btn--active', active);
      el.setAttribute('aria-selected', String(active));
    });

    // Update panels
    this.querySelectorAll('.menu-drawer__tab-panel').forEach((panel) => {
      const el = /** @type {HTMLElement} */ (panel);
      const active = el.dataset.panel === tabId;
      el.classList.toggle('menu-drawer__tab-panel--active', active);
      if (active) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  };
}

if (!customElements.get('drawer-tabs')) {
  customElements.define('drawer-tabs', DrawerTabs);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
